import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import styled from 'styled-components';
import { useResponsiveInViewConfigMargin } from '../../hooks';
import { Button } from './Button';
import { Col } from './Col';
import { Container } from './Container';
import { Grid } from './Grid';
import { SanityBlockContent } from './SanityBlockContent';

// Helper: Extract text from block content.
const getBlockText = (blockContent) => {
  return blockContent?.reduce((text, block) => {
    if (block?._type === 'block') {
      return (
        text +
        block?.children?.reduce(
          (childText, child) => childText + child.text,
          ''
        )
      );
    }
    return text;
  }, '');
};

// Helper: Determine a Tailwind aspect ratio class based on the length of the text.
const getAspectRatioClass = (blockContent, aspectRatio = 'default') => {
  if (aspectRatio === 'none') {
    return '';
  }
  const text = getBlockText(blockContent);
  const textLength = text?.length || 0;
  const shortTextThreshold = 350;
  const mediumTextThreshold = 600;
  // For medium and longer texts, we use the same ratio.
  if (textLength < shortTextThreshold) {
    return 'aspect-w-12 aspect-h-7';
  }
  return 'aspect-w-12 aspect-h-16';
};

const StyledHeader = styled.div`
  #tagline {
    color: ${(props) => props.$spanColor};
  }
  #headline {
    color: ${(props) => props.$h2Color};
  }
`;

const renderContent = (content, colors, sanity) => {
  switch (content?.type) {
    case 'image':
      return (
        <div>
          <GatsbyImage
            image={content?.image?.asset?.gatsbyImageData}
            alt={content?.image?.asset?.alt}
            className={`${getAspectRatioClass(
              content?._rawDescription
            )} h-full w-full rounded-xl object-cover shadow-2xl transition hover:scale-105`}
            loading="lazy"
          />
        </div>
      );
    case 'text':
      return (
        <StyledHeader
          $spanColor={colors?.accent?.dark}
          $h2Color={colors?.secondary?.default}
        >
          <span
            id="tagline"
            className="text-base font-semibold tracking-wider uppercase"
          >
            {content?.tagline2}
          </span>
          <h2
            id="headline"
            className="mx-auto mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl lg:max-w-2xl"
          >
            {content?.headline2}
          </h2>
          <div className="mx-auto mt-8 text-xl max-w-prose">
            <SanityBlockContent
              sanity={sanity}
              blocks={content?._rawDescription2}
            />
          </div>
        </StyledHeader>
      );
    default:
      return (
        <div>
          <GatsbyImage
            image={content?.image?.asset?.gatsbyImageData}
            alt={content?.image?.asset?.alt}
            className={`${getAspectRatioClass(
              content?._rawDescription
            )} h-full w-full rounded-xl object-cover shadow-2xl transition hover:scale-105`}
            loading="lazy"
          />
        </div>
      );
  }
};

const Switcher = ({ sanity, page, section }) => {
  const { colors } = sanity;
  const inViewConfigMargin = useResponsiveInViewConfigMargin();

  const contentRows = section?.content?.map((content, index) => (
    <Grid
      key={content?._key}
      classes="lg:grid-cols-2 gap-x-32 gap-y-14 lg:gap-y-36 lg:max-w-7xl lg:h-full text-left"
      inViewConfigMargin={inViewConfigMargin}
    >
      <Col
        classes={
          index % 2 === 0
            ? 'order-1 lg:order-2 lg:sticky lg:top-44'
            : 'order-1 lg:sticky lg:top-44'
        }
      >
        {renderContent(content, colors, sanity)}
      </Col>
      <Col
        isBgColor={content?.isTextBg}
        color={content?.isTextBg ? colors?.neutral?.white : '#52525b'}
        h2Color={content?.isTextBg ? colors?.secondary?.default : null}
        bgColor={
          content?.isTextBg ? content?.textBgColor?.color : 'transparent'
        }
        classes={
          index % 2 === 0
            ? 'order-2 lg:order-1 lg:sticky lg:top-44'
            : 'order-2 lg:sticky lg:top-44'
        }
      >
        <StyledHeader $spanColor={colors?.accent?.dark}>
          <span
            id="tagline"
            className="text-base font-semibold tracking-wider uppercase"
          >
            {content?.tagline}
          </span>
          <h2 className="mx-auto mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl lg:max-w-2xl">
            {content?.headline}
          </h2>
          <div className="mx-auto mt-8 text-xl max-w-prose">
            <SanityBlockContent
              sanity={sanity}
              blocks={content?._rawDescription}
            />
          </div>
          {content?.ctaShowButton && (
            <div className="mt-12">
              <Button
                sanity={sanity}
                linkType={content?.ctaType}
                internalLink={content?.ctaInternalLink?.slug?.current}
                externalLink={content?.ctaExternalLink}
                label={content?.ctaLabel}
              />
            </div>
          )}
        </StyledHeader>
      </Col>
    </Grid>
  ));

  return (
    <Container
      inViewConfigMargin={inViewConfigMargin}
      classes="my-28 space-y-32"
    >
      {contentRows}
    </Container>
  );
};

Switcher.displayName = 'Switcher';

export { Switcher };
